import React, { useEffect, useState, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { Scissors, X, Sparkles, Calendar, Clock } from 'lucide-react';

const LAUNCH_DATE = new Date('2026-08-15T00:00:00+08:00'); // 15 Aug 2026, Malaysia time
const STORAGE_KEY = 'ewrf_launch_overlay_dismissed_v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const pad = (n) => String(n).padStart(2, '0');

const useCountdown = (target) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = target.getTime() - now.getTime();
  const secs = Math.max(0, Math.floor(diff / 1000));
  return {
    ended: diff <= 0,
    d: Math.floor(secs / 86400),
    h: Math.floor((secs % 86400) / 3600),
    m: Math.floor((secs % 3600) / 60),
    s: secs % 60,
  };
};

const Digit = ({ label, value, color = '#D4AF37' }) => (
  <div className="flex flex-col items-center">
    <div
      className="relative w-16 md:w-20 h-20 md:h-24 rounded-2xl flex items-center justify-center overflow-hidden shadow-2xl"
      style={{
        background: 'linear-gradient(180deg, #0A1628 0%, #1E293B 100%)',
        border: '2px solid rgba(212, 175, 55, 0.4)',
      }}
    >
      <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10"></div>
      <span
        className="text-3xl md:text-5xl font-black tabular-nums"
        style={{ color, textShadow: `0 0 12px ${color}55` }}
        data-testid={`countdown-${label.toLowerCase()}`}
      >
        {pad(value)}
      </span>
    </div>
    <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/60 font-bold mt-2">
      {label}
    </span>
  </div>
);

const LaunchOverlay = () => {
  const { ended, d, h, m, s } = useCountdown(LAUNCH_DATE);
  const [dismissed, setDismissed] = useState(true);
  const [cut, setCut] = useState(false);
  const [cutting, setCutting] = useState(false);
  const [thunder, setThunder] = useState(false);
  const [locked, setLocked] = useState(null); // null = still fetching
  const scissorsRef = useRef(null);
  const audioReady = useRef(false);

  // Fetch server-side launch lock state (and handle ?lock / ?unlock query params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lockCode = params.get('lock');
    const unlockCode = params.get('unlock');
    const doToggle = async (nextLocked, code) => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/content/launch-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, locked: nextLocked }),
        });
        if (!res.ok) {
          // eslint-disable-next-line no-alert
          alert('Invalid lock code');
          const cur = await fetch(`${BACKEND_URL}/api/content/launch-state`).then((r) => r.json()).catch(() => ({}));
          setLocked(!!cur.locked);
          return;
        }
        const data = await res.json();
        setLocked(!!data.locked);
        // Clear any local dismissal on new lock so the ceremony shows again
        if (data.locked) {
          try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
        }
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) {
        console.error('toggle error', e);
        setLocked(false);
      }
    };
    if (lockCode) return void doToggle(true, lockCode);
    if (unlockCode) return void doToggle(false, unlockCode);
    fetch(`${BACKEND_URL}/api/content/launch-state`)
      .then((r) => r.json())
      .then((d) => setLocked(!!d.locked))
      .catch(() => setLocked(false));
  }, []);

  // Local dismissal check runs only when server state resolved
  useEffect(() => {
    if (locked === null) return;
    if (!locked) {
      setDismissed(true);
      return;
    }
    try {
      const already = window.localStorage.getItem(STORAGE_KEY);
      setDismissed(!!already);
    } catch {
      setDismissed(false);
    }
  }, [locked]);

  // Lock body scroll while overlay is visible
  useEffect(() => {
    if (dismissed) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dismissed]);

  const persistDismiss = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  }, []);

  const fireConfetti = () => {
    // Grand fireworks + firecracker sequence
    const grandColors = ['#D4AF37', '#FFD54F', '#ED1C24', '#FF6B00', '#1976D2', '#25D366', '#E91E63', '#FFC107'];

    // 1) Massive central explosion (thunder-scale burst)
    confetti({
      particleCount: 250,
      spread: 360,
      ticks: 200,
      gravity: 0.5,
      startVelocity: 60,
      origin: { x: 0.5, y: 0.5 },
      colors: grandColors,
      scalar: 1.2,
    });

    // 2) Side cannon bursts (like grand cannons at a ceremony)
    setTimeout(() => {
      confetti({ particleCount: 150, angle: 60, spread: 80, startVelocity: 65, origin: { x: 0, y: 0.75 }, colors: grandColors, scalar: 1.1 });
      confetti({ particleCount: 150, angle: 120, spread: 80, startVelocity: 65, origin: { x: 1, y: 0.75 }, colors: grandColors, scalar: 1.1 });
    }, 200);

    // 3) Multiple firework bursts at random positions (skyburst effect)
    let burstCount = 0;
    const fireworkInterval = setInterval(() => {
      const x = 0.15 + Math.random() * 0.7;
      const y = 0.15 + Math.random() * 0.35;
      confetti({
        particleCount: 80,
        spread: 360,
        startVelocity: 35,
        ticks: 150,
        gravity: 0.7,
        origin: { x, y },
        colors: [grandColors[burstCount % grandColors.length], '#FFFFFF', '#FFD54F'],
        scalar: 0.9 + Math.random() * 0.5,
      });
      burstCount++;
      if (burstCount >= 12) clearInterval(fireworkInterval);
    }, 400);

    // 4) Rapid-fire firecracker rain (loud crackling effect)
    let rainCount = 0;
    const rainId = setInterval(() => {
      // Multi-column rain
      for (let i = 0; i < 3; i++) {
        confetti({
          particleCount: 20,
          angle: 90,
          spread: 70,
          startVelocity: 55,
          origin: { x: Math.random(), y: -0.1 },
          gravity: 1.0,
          ticks: 200,
          colors: grandColors,
          scalar: 0.8,
        });
      }
      rainCount++;
      if (rainCount > 15) clearInterval(rainId);
    }, 300);

    // 5) Grand finale (7 seconds later) — chained sparklers on both sides
    setTimeout(() => {
      let finaleCount = 0;
      const finaleId = setInterval(() => {
        confetti({
          particleCount: 50,
          angle: 90,
          spread: 120,
          startVelocity: 45,
          origin: { x: 0.5, y: 0.9 },
          colors: ['#D4AF37', '#FFD54F', '#FFFFFF'],
          shapes: ['star'],
          scalar: 1.4,
        });
        finaleCount++;
        if (finaleCount > 6) clearInterval(finaleId);
      }, 200);
    }, 6500);
  };

  const triggerThunder = () => {
    setThunder(true);
    // Multi-flash thundershot sequence — 5 rapid flashes with varying delays
    const flashes = [180, 220, 200, 180, 240];
    let acc = 0;
    flashes.forEach((dur, i) => {
      acc += dur;
      setTimeout(() => {
        setThunder(false);
        if (i < flashes.length - 1) {
          setTimeout(() => setThunder(true), 60 + Math.random() * 80);
        }
      }, acc);
    });
  };

  const fireCrackerShots = () => {
    // Rapid sequential mini-bursts across the screen — sounds/looks like a strip of firecrackers going off
    const positions = [
      { x: 0.1, y: 0.85 }, { x: 0.9, y: 0.85 },
      { x: 0.2, y: 0.75 }, { x: 0.8, y: 0.75 },
      { x: 0.3, y: 0.9 },  { x: 0.7, y: 0.9 },
      { x: 0.5, y: 0.8 },
      { x: 0.15, y: 0.65 }, { x: 0.85, y: 0.65 },
      { x: 0.4, y: 0.85 }, { x: 0.6, y: 0.85 },
    ];
    positions.forEach((pos, i) => {
      setTimeout(() => {
        confetti({
          particleCount: 40,
          spread: 100,
          startVelocity: 55,
          ticks: 80,
          gravity: 0.8,
          origin: pos,
          colors: ['#FFD54F', '#FF6B00', '#ED1C24', '#FFFFFF'],
          scalar: 0.9,
          shapes: ['circle', 'square'],
        });
        // Small thunder flash on every 3rd cracker
        if (i % 3 === 0) {
          setThunder(true);
          setTimeout(() => setThunder(false), 100);
        }
      }, i * 120);
    });
  };

  const handleCut = () => {
    if (cutting || cut) return;
    setCutting(true);
    // Thunder flash first (dramatic build-up)
    triggerThunder();
    // Ribbon-cut animation + fireworks + firecrackers
    setTimeout(() => {
      setCut(true);
      triggerThunder();
      fireConfetti();
      // Firecracker strip runs alongside fireworks
      setTimeout(() => fireCrackerShots(), 400);
      // Second wave of firecrackers after 3 seconds
      setTimeout(() => fireCrackerShots(), 3000);
      // Third thundershot at 5 seconds
      setTimeout(() => triggerThunder(), 5200);
    }, 900);
  };

  const closeOverlay = () => {
    fireConfetti();
    setTimeout(persistDismiss, 400);
  };

  if (dismissed) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 overflow-hidden animate-fade-in"
      data-testid="launch-overlay"
      style={{ background: 'rgba(10, 22, 40, 0.94)', backdropFilter: 'blur(8px)' }}
    >
      {/* Thunder flash overlay */}
      {thunder && (
        <div
          className="absolute inset-0 pointer-events-none z-[80]"
          style={{
            background: 'radial-gradient(circle at 50% 40%, rgba(255,255,255,0.95) 0%, rgba(255,235,150,0.7) 30%, transparent 70%)',
            animation: 'ewrfThunderFlash 180ms ease-out',
          }}
        />
      )}
      {/* subtle stars */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        backgroundImage: 'radial-gradient(2px 2px at 20% 30%, #fff, transparent), radial-gradient(2px 2px at 80% 60%, #fff, transparent), radial-gradient(2px 2px at 40% 80%, #FFD54F, transparent), radial-gradient(1.5px 1.5px at 65% 20%, #fff, transparent), radial-gradient(1.5px 1.5px at 15% 65%, #FFD54F, transparent)',
        backgroundSize: '600px 600px',
      }}></div>

      <button
        onClick={persistDismiss}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="launch-overlay-close"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative w-full max-w-3xl text-center">
        {/* Header */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD54F]/20 border border-[#D4AF37]/40 mb-6">
          <Sparkles className="w-4 h-4 text-[#FFD54F]" />
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#FFD54F]">
            EWRF Malaysia
          </span>
        </div>

        <h1 className="text-4xl md:text-6xl font-serif text-white mb-3 leading-tight">
          {ended ? (
            <>The New Website is <span className="italic text-[#D4AF37]">Live</span></>
          ) : (
            <>The Grand Unveiling <span className="italic text-[#D4AF37]">Awaits</span></>
          )}
        </h1>
        <p className="text-white/70 text-sm md:text-base mb-8 max-w-xl mx-auto">
          {ended
            ? 'The moment has arrived. Cut the ceremonial ribbon and witness the official unveiling of the redesigned EWRF Malaysia website — a new chapter, 47 years in the making.'
            : 'A new digital era for EWRF Malaysia — officially unveiled by our President on 15 August 2026. Countdown begins.'}
        </p>

        {!ended && (
          <>
            <div className="flex items-center justify-center gap-3 md:gap-6 mb-8">
              <Digit label="Days" value={d} color="#D4AF37" />
              <span className="text-3xl md:text-5xl font-black text-[#D4AF37]/60">:</span>
              <Digit label="Hours" value={h} color="#FFD54F" />
              <span className="text-3xl md:text-5xl font-black text-[#D4AF37]/60">:</span>
              <Digit label="Minutes" value={m} color="#FFD54F" />
              <span className="text-3xl md:text-5xl font-black text-[#D4AF37]/60">:</span>
              <Digit label="Seconds" value={s} color="#ED1C24" />
            </div>

            <div className="inline-flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-3 text-white/80 text-xs md:text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-semibold">15 August 2026</span>
              </div>
              <div className="w-px h-4 bg-white/20"></div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-semibold">Malaysia Time</span>
              </div>
            </div>

            <p className="mt-8 text-center text-xs text-white/50 uppercase tracking-[0.25em]">
              🔒 The site opens once our President cuts the ribbon
            </p>
          </>
        )}

        {ended && !cut && (
          <>
            {/* Ribbon */}
            <div className="relative mt-4 mb-8 mx-auto max-w-2xl h-24 md:h-32 select-none">
              <div className="absolute inset-y-0 -left-8 -right-8 flex items-center">
                <div
                  className="w-full h-10 md:h-14 relative"
                  style={{
                    background: 'linear-gradient(180deg, #ED1C24 0%, #B71C1C 50%, #7f0000 100%)',
                    boxShadow: '0 10px 40px rgba(237, 28, 36, 0.5), inset 0 -6px 12px rgba(0,0,0,0.4)',
                  }}
                >
                  <div className="absolute inset-x-0 top-0 h-1 bg-white/30"></div>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30"></div>
                </div>
              </div>
              {/* Scissors icon animating from left */}
              <div
                ref={scissorsRef}
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-700 ease-out ${
                  cutting ? 'scale-125 rotate-12' : 'scale-100'
                }`}
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8b6914] flex items-center justify-center shadow-2xl border-4 border-white/30">
                  <Scissors className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
              </div>
            </div>

            <button
              onClick={handleCut}
              disabled={cutting}
              className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-black uppercase tracking-widest text-sm md:text-base shadow-2xl transition-all ${
                cutting
                  ? 'bg-gradient-to-r from-[#8b6914] to-[#D4AF37] text-white/70 cursor-wait'
                  : 'bg-gradient-to-r from-[#D4AF37] via-[#FFD54F] to-[#D4AF37] text-[#0A1628] hover:scale-105 hover:shadow-[0_0_40px_rgba(212,175,55,0.6)]'
              }`}
              data-testid="launch-cut-ribbon-btn"
            >
              <Scissors className={`w-5 h-5 ${cutting ? 'animate-pulse' : ''}`} />
              {cutting ? 'Unveiling…' : 'Cut the Ribbon — Launch!'}
            </button>

            <p className="text-white/50 text-xs mt-6">
              A single click by our President officially opens EWRF Malaysia's new digital home.
            </p>
          </>
        )}

        {ended && cut && (
          <div className="animate-fade-in">
            {/* Cut ribbon (two halves flying apart) */}
            <div className="relative mt-4 mb-8 mx-auto max-w-2xl h-24 md:h-32 select-none overflow-visible">
              <div className="absolute inset-y-0 -left-8 flex items-center w-1/2">
                <div
                  className="w-full h-10 md:h-14 origin-right transform rotate-[-8deg] -translate-x-4"
                  style={{
                    background: 'linear-gradient(180deg, #ED1C24 0%, #B71C1C 50%, #7f0000 100%)',
                    boxShadow: '0 10px 40px rgba(237, 28, 36, 0.4)',
                  }}
                ></div>
              </div>
              <div className="absolute inset-y-0 -right-8 flex items-center w-1/2 justify-end">
                <div
                  className="w-full h-10 md:h-14 origin-left transform rotate-[8deg] translate-x-4"
                  style={{
                    background: 'linear-gradient(180deg, #ED1C24 0%, #B71C1C 50%, #7f0000 100%)',
                    boxShadow: '0 10px 40px rgba(237, 28, 36, 0.4)',
                  }}
                ></div>
              </div>
              {/* Sparkle centerpiece */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[#FFD54F] via-[#D4AF37] to-[#8b6914] flex items-center justify-center shadow-[0_0_60px_rgba(212,175,55,0.9)] border-4 border-white/40">
                  <Sparkles className="w-10 h-10 md:w-12 md:h-12 text-white" />
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-[#D4AF37]/30 to-[#FFD54F]/30 border border-[#D4AF37] mb-4 animate-pulse">
              <Sparkles className="w-4 h-4 text-[#FFD54F]" />
              <span className="text-sm font-black uppercase tracking-widest text-[#FFD54F]">
                Officially Unveiled
              </span>
              <Sparkles className="w-4 h-4 text-[#FFD54F]" />
            </div>

            <h2 className="text-3xl md:text-5xl font-serif text-white mb-3">
              A New Era for <span className="italic text-[#D4AF37]">EWRF Malaysia</span>
            </h2>
            <p className="text-white/70 max-w-lg mx-auto mb-8 text-sm md:text-base">
              47 years of impact — now with a new digital home. Explore our programmes, meet our
              committee, and join us in changing lives across Malaysia.
            </p>

            <button
              onClick={closeOverlay}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-black uppercase tracking-widest text-sm bg-white text-[#0A1628] hover:bg-[#D4AF37] hover:text-white transition-all shadow-xl"
              data-testid="launch-enter-site-btn"
            >
              Enter the Site
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LaunchOverlay;
